import { InitDetail } from 'lightgallery/lg-events'
import { LightGallery } from 'lightgallery/lightgallery'
import useTranslation from 'next-translate/useTranslation'
import React, { useCallback, useMemo, useRef, useState } from 'react'
import ReactDOM from 'react-dom'

import { PropertyListingStatus, SquareFootage } from '@unreserved-frontend-v2/api/generated/graphql/types'
import { getAddressCaption, getSimpleAddress, getStreetCaption } from '@unreserved-frontend-v2/geolocation/utils/utils'
import { MediaProps } from '@unreserved-frontend-v2/modules/listings/components/[tab]/media/utils/media-props'
import { CropType, getResizedImage } from '@unreserved-frontend-v2/ui/image-with-shimmer/utils'
import { InfoItem, InfoSet } from '@unreserved-frontend-v2/ui/info-set/info-set'
import { currency } from '@unreserved-frontend-v2/utils/currency'

import { AugmentedSquareFootageInfo } from '../../../augmented-square-footage-info/augmented-square-footage-info'

export const useGalleryDesktop = ({
  isRestricted,
  address,
  beds,
  baths,
  squareFootage,
  squareFootageDisplay,
  status,
  salePriceAmount,
  listingPriceAmount,
  buyerCashbackAmount,
  media,
}: MediaProps) => {
  const lightGallery = useRef<LightGallery | null>(null)
  const { t } = useTranslation()

  const onInit = useCallback((detail: InitDetail) => {
    if (detail) {
      lightGallery.current = detail.instance
    }
  }, [])

  const openGallery = useCallback((slideIndex: number) => {
    lightGallery.current?.openGallery(slideIndex)
  }, [])

  const infoItems = useMemo(() => {
    const items: InfoItem[] = []

    if (beds != null) {
      items.push({ key: 'listings:beds', value: beds })
    }

    if (baths != null) {
      items.push({ key: 'listings:baths', value: baths })
    }

    if (squareFootage != null) {
      items.push({
        key: 'listings:sq-ft',
        value: squareFootage?.approximateSquareFootage || squareFootage?.derivedSquareFootage || squareFootageDisplay,
        subValue: (
          <AugmentedSquareFootageInfo
            squareFootage={(squareFootage || squareFootageDisplay) as SquareFootage}
            variant="gallery"
          />
        ),
      })
    }

    items.push(
      status === PropertyListingStatus.Sold && !isRestricted && salePriceAmount !== listingPriceAmount
        ? {
            key: 'listings:listing-price',
            subValue: currency(listingPriceAmount),
            subValueClass: 'line-through',
            value: currency(salePriceAmount),
            valueClass: 'text-primary',
          }
        : {
            key: `listings:${status === PropertyListingStatus.Sold ? 'sale-price' : 'listing-price'}`,
            value: currency(listingPriceAmount),
            valueClass: 'text-primary',
            ...(buyerCashbackAmount
              ? {
                  subValue: t('listings:amount-rebate', { amount: currency(buyerCashbackAmount) }),
                  subValueClass: 'text-primary font-bold',
                }
              : null),
          }
    )

    return items
  }, [
    beds,
    baths,
    squareFootage,
    status,
    isRestricted,
    salePriceAmount,
    listingPriceAmount,
    buyerCashbackAmount,
    t,
    squareFootageDisplay,
  ])

  /**
   * NOTE:
   * `useState` is necessary instead of `useRef` to force rerenders when switching between tabs & reopening the photogallery. This is
   * because the lightgallery library is not rendered using the same React virtual dom and the header needs to be forcefully rerendered
   **/
  const [renderRef, setRenderRef] = useState<HTMLDivElement | null>(null)

  const galleryHeader = (
    <div
      ref={(ref) => {
        setRenderRef(ref)
      }}
      className="hidden"
    >
      {renderRef
        ? ReactDOM.createPortal(
            <InfoSet
              image="/static/images/map.png"
              infoItems={infoItems}
              caption={getAddressCaption(address, true)}
              title={getStreetCaption(address)}
              dividerClass="bg-shades-600"
              isRestricted={undefined}
            />,
            renderRef as Element
          )
        : null}
    </div>
  )

  const lightBoxItems = useMemo(
    () =>
      media?.map((item, i) => {
        return {
          src: item.originUrl,
          subHtml: '<span id="react-gallery-header" class="relative" />',
          thumb: getResizedImage(item.originUrl, 124, 104, CropType.FIT),
          alt: `${getSimpleAddress(address)} - ${t('ui:of', {
            current: i + 1,
            total: media?.length,
          })}`,
        }
      }),
    [media, address, t]
  )

  const onAfterAppendSubHtml = useCallback(() => {
    /**
     * NOTE: This is a gallary modal fix. When a nested MUI modal closes
     * and the scrolling element is 0, the ENTIRE gallary closes as well
     **/
    if (renderRef) {
      if (document.scrollingElement?.scrollTop === 0) {
        document.scrollingElement.scrollTop = 1
      }

      renderRef.classList.toggle('hidden', false)
      document.getElementById('react-gallery-header')?.append(renderRef as Element)
    }
  }, [renderRef])

  return {
    lightBoxItems,
    galleryHeader,
    onInit,
    onAfterAppendSubHtml,
    openGallery,
  }
}
